import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs, query, orderBy, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { enrichCustomerDocForCreate } from '../utils/customerPortal';
import { fetchOrdersForCustomer } from '../utils/customerOrders';
import CustomerForm from '../components/CustomerForm';
import CustomerPortalPanel from '../components/CustomerPortalPanel';
import SearchInput from '../components/SearchInput';
import CustomerOrdersModal from '../components/CustomerOrdersModal';

const PageContainer = styled.div`
  padding: 80px 1% 20px 1%;
  width: 98%;
  margin: 0 auto;
  background: var(--al-bg);
  min-height: 100vh;
`;

const PageHeader = styled.h1`
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
  width: 50%;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  background: ${props => props.theme.colors.navbar};
  border-radius: 0;
  padding: 1px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: transparent;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${props => props.theme.colors.navbar};
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: white;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 0.02em;
`;

const ActionButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
  padding: 8px 12px;
  text-transform: uppercase;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-right: 8px;

  &:hover {
    background: #7ACC7A;
  }
`;

const AddButton = styled(ActionButton)`
  padding: 10px 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  padding: 30px;
  border-radius: 0;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  > form {
    background: var(--al-surface);
    padding: 24px;
  }
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(var(--al-primary-rgb), 0.05);
    cursor: pointer;
  }
`;

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerOrders, setSelectedCustomerOrders] = useState([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCustomers = async () => {
    try {
      console.log('Fetching customers...');
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, orderBy('client'));
      const querySnapshot = await getDocs(q);
      
      const customerList = [];
      querySnapshot.forEach((doc) => {
        console.log('Found customer:', doc.data());
        customerList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('Total customers:', customerList.length);
      setCustomers(customerList);
      setFilteredCustomers(customerList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const filtered = customers.filter(customer => 
      customer.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.contact?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredCustomers(filtered);
  }, [searchQuery, customers]);

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleAddCustomer = async (customerData) => {
    try {
      // Add the customer to Firestore
      await addDoc(collection(db, 'customers'), enrichCustomerDocForCreate({
        ...customerData,
        createdAt: new Date()
      }));

      // Close the modal
      setIsAddModalOpen(false);

      // Refresh the customer list
      fetchCustomers();

      // Optional: Add success message
      alert('Customer added successfully!');
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Error adding customer. Please try again.');
    }
  };

  const handleUpdateCustomer = async (updatedData) => {
    try {
      const customerRef = doc(db, 'customers', selectedCustomer.id);
      await updateDoc(customerRef, updatedData);
      
      // Close the modal
      setIsEditModalOpen(false);
      setSelectedCustomer(null);

      // Refresh the customer list
      fetchCustomers();

      // Show success animation
      setShowSuccess(true);
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Error updating customer. Please try again.');
    }
  };

  const fetchCustomerOrders = async (customer) => {
    try {
      const ordersWithLines = await fetchOrdersForCustomer(db, customer, {
        allCustomers: customers,
        includeLines: true
      });

      setSelectedCustomerOrders(ordersWithLines);
      setSelectedCustomerName(customer.client);
      setIsOrdersModalOpen(true);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      alert('Error loading orders');
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <PageHeader>CUSTOMERS</PageHeader>
        <AddButton onClick={() => setIsAddModalOpen(true)}>
          Add New Customer
        </AddButton>
      </HeaderContainer>

      <FiltersContainer>
        <SearchInput
          placeholder="SEARCH CUSTOMERS..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </FiltersContainer>

      {isAddModalOpen && (
        <Modal onClick={() => setIsAddModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h2>Add New Customer</h2>
            </ModalHeader>
            <CustomerForm 
              onSubmit={handleAddCustomer} 
              onCancel={() => setIsAddModalOpen(false)} 
            />
          </ModalContent>
        </Modal>
      )}

      {isEditModalOpen && selectedCustomer && (
        <Modal onClick={() => setIsEditModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h2>Edit Customer</h2>
              <CloseButton onClick={() => setIsEditModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <CustomerForm 
              initialData={selectedCustomer}
              onSubmit={handleUpdateCustomer}
              onCancel={() => setIsEditModalOpen(false)}
              submitButtonText="Update Customer"
            />
            <CustomerPortalPanel customer={selectedCustomer} onUpdated={fetchCustomers} />
          </ModalContent>
        </Modal>
      )}

      {loading ? (
        <p>Loading customers...</p>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Client</Th>
                <Th>Contact</Th>
                <Th>Email</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => (
                <TableRow key={customer.id}>
                  <Td>{customer.client}</Td>
                  <Td>{customer.contact}</Td>
                  <Td>{customer.email}</Td>
                  <Td>
                    <ButtonGroup>
                      <ActionButton onClick={() => handleEditCustomer(customer)}>
                        Edit Customer
                      </ActionButton>
                      <ActionButton onClick={() => fetchCustomerOrders(customer)}>
                        View Orders
                      </ActionButton>
                    </ButtonGroup>
                  </Td>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

      <CustomerOrdersModal
        isOpen={isOrdersModalOpen}
        onClose={() => setIsOrdersModalOpen(false)}
        orders={selectedCustomerOrders}
        customerName={selectedCustomerName}
      />
    </PageContainer>
  );
}

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    color: white;
    font-family: 'Montserrat', sans-serif;
    font-size: 24px;
    font-weight: 600;
    text-transform: uppercase;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

export default Customers; 